import React, { useEffect, useState } from "react"
import {Menu, X, ArrowRight, Mail, Linkedin, Feather, Award, BookOpen, Stethoscope, Users, Download, Send, Phone, MapPin, ExternalLink} from 'lucide-react'
import { motion, useScroll, useTransform } from "framer-motion"

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState("home")
  const { scrollY } = useScroll()
  const heroScale = useTransform(scrollY, [0, 300], [1, 0.95])

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  })

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "expertise", "credentials", "publications", "poetry", "work", "testimonials", "contact"]
      const current = sections.find(section => {
        const element = document.getElementById(section)
        if (element) {
          const rect = element.getBoundingClientRect()
          return rect.top <= 100 && rect.bottom >= 100
        }
        return false
      })
      if (current) setActiveSection(current)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    element?.scrollIntoView({ behavior: "smooth" })
    setIsMenuOpen(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Form submission logic would go here
    const mailtoLink = `mailto:drawais.ent@gmail.com?subject=Contact from ${formData.name}&body=${formData.message}%0D%0A%0D%0AFrom: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}`
    window.location.href = mailtoLink
  }

  return (
    <div className="bg-stone-50 text-neutral-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-50/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-xl font-light tracking-wide cursor-pointer"
              onClick={() => scrollToSection("home")}
            >
              Dr. Muhammad Awais Amin
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {["home", "about", "expertise", "credentials", "work", "contact"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`text-sm uppercase tracking-widest transition-colors ${
                    activeSection === item ? "text-amber-700" : "text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden bg-stone-50 border-t border-stone-200"
          >
            <div className="px-6 py-8 space-y-6">
              {["home", "about", "expertise", "credentials", "work", "contact"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block text-lg uppercase tracking-widest text-neutral-600 hover:text-neutral-900"
                >
                  {item}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-6 lg:px-12 pt-20">
        <div className="max-w-6xl w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <p className="text-amber-700 text-sm uppercase tracking-widest">Welcome</p>
                <h1 className="text-5xl lg:text-7xl font-light leading-tight">
                  Surgeon &
                  <br />
                  <span className="italic font-serif">Healthcare Innovator</span>
                </h1>
              </div>
              <p className="text-lg text-neutral-600 leading-relaxed max-w-lg">
                Bridging clinical excellence with healthcare technology. Fellow of the American College of Surgeons, CEO of CodifyCare & Founder of NursingHive.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection("contact")}
                  className="bg-neutral-900 text-white px-8 py-4 rounded-full hover:bg-neutral-800 transition-colors flex items-center gap-2 group"
                >
                  Schedule Consultation
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollToSection("work")}
                  className="border-2 border-neutral-900 text-neutral-900 px-8 py-4 rounded-full hover:bg-neutral-900 hover:text-white transition-colors"
                >
                  View Projects
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-amber-100 to-stone-200 shadow-2xl">
                <img
                  src="https://i.imghippo.com/files/VcT2735UIQ.jpg"
                  alt="Dr. Muhammad Awais Amin"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-amber-700 rounded-full blur-3xl opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <p className="text-amber-700 text-sm uppercase tracking-widest">About Me</p>
              <h2 className="text-4xl lg:text-5xl font-light leading-tight">
                Building <span className="italic font-serif">Systems</span> That Heal
              </h2>
              <div className="space-y-4 text-neutral-600 leading-relaxed">
                <p>
                  With 17+ years of medical experience and 14+ years specializing in ENT surgery, I've dedicated my career to improving healthcare delivery through both clinical excellence and technological innovation.
                </p>
                <p>
                  As a practicing surgeon at Jaber Al-Ahmed Hospital in Kuwait and CEO of two healthcare technology companies, I bridge the gap between clinical needs and digital solutions to empower clinicians, improve efficiency, and elevate the standard of care.
                </p>
                <p>
                  My mission is to build systems that not only treat patients but transform how healthcare professionals work—reducing administrative burden while enhancing patient outcomes.
                </p>
              </div>
              <button
                onClick={() => scrollToSection("credentials")}
                className="mt-4 text-amber-700 hover:text-amber-800 flex items-center gap-2 group font-medium"
              >
                View Full Credentials
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { label: "Years Medical Experience", value: "17+", isIcon: false },
                { label: "Years ENT Specialty", value: "14+", isIcon: false },
                { label: "Writer & Poet", value: "icon", isIcon: true },
                { label: "Healthcare Companies", value: "2", isIcon: false }
              ].map((stat, index) => (
                <motion.div 
                  key={index} 
                  className="bg-stone-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="text-4xl font-light text-amber-700 mb-2">
                    {stat.isIcon ? <Feather size={40} strokeWidth={1.5} /> : stat.value}
                  </div>
                  <div className="text-sm text-neutral-600 uppercase tracking-wider">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Expertise</p>
            <h2 className="text-4xl lg:text-5xl font-light">Areas of Excellence</h2>
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto">
              Specialized ENT surgical expertise combined with healthcare technology innovation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Endoscopic Sinus Surgery",
                description: "Advanced functional endoscopic sinus surgery (FESS) for chronic sinusitis, nasal polyps, and sinus disorders.",
                icon: Stethoscope
              },
              {
                title: "Head & Neck Surgery",
                description: "Comprehensive surgical treatment for head and neck conditions including thyroid surgery and neck masses.",
                icon: Stethoscope
              },
              {
                title: "Laryngeal Procedures",
                description: "Expert care for voice disorders, laryngeal pathology, and advanced airway management.",
                icon: Stethoscope
              },
              {
                title: "Thyroid Surgery",
                description: "Precision thyroid and parathyroid surgery with focus on minimizing complications and optimal outcomes.",
                icon: Stethoscope
              },
              {
                title: "Emergency ENT Care",
                description: "Immediate surgical intervention for ENT emergencies including airway obstruction and trauma.",
                icon: Stethoscope
              },
              {
                title: "Aesthetic Procedures",
                description: "Cosmetic and functional nasal surgery including septoplasty and rhinoplasty procedures.",
                icon: Stethoscope
              }
            ].map((service, index) => {
              const Icon = service.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl hover:shadow-xl transition-all hover:-translate-y-1 border border-stone-100"
                >
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon size={24} className="text-amber-700" />
                  </div>
                  <h3 className="text-xl font-light mb-4">{service.title}</h3>
                  <p className="text-neutral-600 leading-relaxed text-sm">{service.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section id="credentials" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Credentials</p>
            <h2 className="text-4xl lg:text-5xl font-light">Qualifications & Certifications</h2>
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto">
              Board-certified surgeon with international qualifications and professional memberships
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Medical Degrees */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-stone-50 p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center">
                  <Award size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-light">Medical Degrees</h3>
              </div>
              <ul className="space-y-3 text-neutral-600">
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>MBBS</strong> - Bachelor of Medicine & Bachelor of Surgery</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>FCPS</strong> - Fellow of College of Physicians & Surgeons, Pakistan</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>Diplomat</strong> - American Academy of Aesthetic Medicine</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>CHPE</strong> - Certificate in Health Professions Education</span>
                </li>
              </ul>
            </motion.div>

            {/* Board Certifications */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-stone-50 p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center">
                  <Award size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-light">Board Certifications</h3>
              </div>
              <ul className="space-y-3 text-neutral-600">
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>FACS</strong> - Fellow, American College of Surgeons</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>ENT Specialist</strong> - Otorhinolaryngology & Head-Neck Surgery</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>Licensed Surgeon</strong> - Pakistan Medical & Dental Council</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>Kuwait Medical License</strong> - Active clinical practice</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span><strong>Aesthetic Medicine</strong> - Certified practitioner</span>
                </li>
              </ul>
            </motion.div>

            {/* Professional Memberships */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-stone-50 p-8 rounded-2xl md:col-span-2"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-white" />
                </div>
                <h3 className="text-2xl font-light">Professional Memberships</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-neutral-600">
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>Pakistan Society of Otorhinolaryngology (PSORL)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>Society of Rhinology (SORESS)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>European Society of Surgical Oncology (ESSO)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>Association of Pakistani Thyroid Surgeons (APTS)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>International Sinus Society (ISS)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-amber-700 mt-1">•</span>
                  <span>American College of Surgeons</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Current Position */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-br from-amber-50 to-orange-50 p-10 rounded-3xl border border-amber-100"
          >
            <div className="flex items-start gap-4 mb-4">
              <MapPin className="text-amber-700 mt-1" size={24} />
              <div>
                <h3 className="text-2xl font-light mb-2">Current Position</h3>
                <p className="text-lg text-neutral-700 font-medium">Specialist Surgeon</p>
                <p className="text-neutral-600">Department of Otorhinolaryngology & Head-Neck Surgery</p>
                <p className="text-neutral-600">Jaber Al-Ahmed Al-Sabah Hospital, Kuwait</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Publications Section */}
      <section id="publications" className="py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Research</p>
            <h2 className="text-4xl lg:text-5xl font-light">Publications & Academic Work</h2>
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto">
              Contributing to medical literature with research in ENT surgery, medical education, and healthcare innovation
            </p>
          </motion.div>

          <div className="grid gap-8 max-w-4xl mx-auto">
            {[
              {
                title: "Advanced Endoscopic Techniques in Sinus Surgery",
                journal: "Journal of Otorhinolaryngology",
                description: "Comprehensive analysis of modern endoscopic approaches for complex sinus disease management."
              },
              {
                title: "Healthcare Technology Integration in Clinical Practice",
                journal: "Medical Innovation Quarterly",
                description: "Examining the impact of digital workflow solutions on clinical efficiency and patient outcomes."
              },
              {
                title: "Medical Education in the Digital Age",
                journal: "Health Professions Education Journal",
                description: "Innovative approaches to medical training leveraging technology and evidence-based teaching methods."
              }
            ].map((publication, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-stone-200 hover:shadow-lg transition-all hover:border-amber-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <BookOpen size={20} className="text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-light mb-2">{publication.title}</h3>
                    <p className="text-sm text-amber-700 mb-3 italic">{publication.journal}</p>
                    <p className="text-neutral-600 text-sm leading-relaxed">{publication.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button className="text-amber-700 hover:text-amber-800 flex items-center gap-2 group mx-auto font-medium">
              View All Publications
              <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Poetry & Writing Section */}
      <section id="poetry" className="py-32 px-6 lg:px-12 bg-gradient-to-br from-amber-50 to-stone-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Literary Work</p>
            <h2 className="text-4xl lg:text-5xl font-light">
              <span className="italic font-serif">Poetry & Writing</span>
            </h2>
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto">
              Exploring the human experience through words—where medicine meets art
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-3xl shadow-lg"
            >
              <Feather size={32} className="text-amber-700 mb-6" strokeWidth={1.5} />
              <h3 className="text-2xl font-light italic font-serif mb-4">Poetry Collections</h3>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Crafting verses that explore the intersection of healing, humanity, and the medical journey. My poetry reflects on life, loss, hope, and the profound responsibility of caring for others.
              </p>
              <button className="text-amber-700 hover:text-amber-800 flex items-center gap-2 group font-medium">
                Read Poetry
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-10 rounded-3xl shadow-lg"
            >
              <BookOpen size={32} className="text-amber-700 mb-6" strokeWidth={1.5} />
              <h3 className="text-2xl font-light italic font-serif mb-4">Medical Writing</h3>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Translating complex medical concepts into accessible insights. Writing about healthcare innovation, surgical excellence, and the future of medical practice for both professionals and patients.
              </p>
              <button className="text-amber-700 hover:text-amber-800 flex items-center gap-2 group font-medium">
                Explore Articles
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work Section */}
      <section id="work" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Portfolio</p>
            <h2 className="text-4xl lg:text-5xl font-light">Projects & Ventures</h2>
            <p className="text-neutral-600 mt-6 max-w-2xl mx-auto">
              Transforming healthcare through clinical excellence and innovative technology solutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Clinical Practice",
                subtitle: "Jaber Al-Ahmed Hospital, Kuwait",
                description: "Otorhinolaryngology & Head-Neck Surgery Department. Specialized ENT surgical care with focus on endoscopic procedures and advanced head-neck surgery.",
                gradient: "from-blue-100 to-cyan-100",
                link: null
              },
              {
                title: "CodifyCare",
                subtitle: "Healthcare Technology Platform",
                description: "Revolutionary billing and documentation solutions helping healthcare practices reduce administrative burden and improve workflow efficiency.",
                gradient: "from-amber-100 to-orange-100",
                link: "https://www.codifycare.com"
              },
              {
                title: "NursingHive",
                subtitle: "Professional Education Platform",
                description: "Empowering nurses worldwide with structured learning, evidence-based resources, and clinical guidance for professional excellence.",
                gradient: "from-green-100 to-emerald-100",
                link: "https://www.nursinghive.com"
              },
              {
                title: "Research & Publications",
                subtitle: "Academic Contributions",
                description: "Published research in ENT surgery, medical education, and healthcare innovation. Active speaker at international medical conferences.",
                gradient: "from-purple-100 to-pink-100",
                link: null
              }
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => project.link && window.open(project.link, '_blank')}
              >
                <div className={`aspect-[4/3] bg-gradient-to-br ${project.gradient} rounded-2xl overflow-hidden mb-6 flex items-center justify-center p-8 group-hover:shadow-xl transition-all`}>
                  <div className="text-center">
                    <div className="text-4xl font-light text-neutral-800 mb-2">{project.title}</div>
                    <div className="text-sm text-neutral-600">{project.subtitle}</div>
                    {project.link && (
                      <ExternalLink className="mx-auto mt-4 text-neutral-600 group-hover:text-amber-700 transition-colors" size={24} />
                    )}
                  </div>
                </div>
                <h3 className="text-2xl font-light mb-2 group-hover:text-amber-700 transition-colors">
                  {project.title}
                </h3>
                <p className="text-neutral-600">{project.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-amber-700 text-sm uppercase tracking-widest mb-4">Get In Touch</p>
            <h2 className="text-4xl lg:text-6xl font-light leading-tight">
              Let's Connect and
              <br />
              <span className="italic font-serif">Transform Healthcare</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto mt-6">
              Whether you're seeking clinical consultation, interested in healthcare innovation, or exploring partnership opportunities - I'd be delighted to connect.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-stone-50 p-8 rounded-2xl"
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm uppercase tracking-wider text-neutral-600 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-700 transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm uppercase tracking-wider text-neutral-600 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-700 transition-colors"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm uppercase tracking-wider text-neutral-600 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-700 transition-colors"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm uppercase tracking-wider text-neutral-600 mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-700 transition-colors resize-none"
                    placeholder="Tell me about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-neutral-900 text-white px-8 py-4 rounded-full hover:bg-amber-700 transition-colors flex items-center justify-center gap-2 group"
                >
                  Send Message
                  <Send size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl font-light mb-8">Direct Contact</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-wider text-neutral-500 mb-1">Email</p>
                      <a href="mailto:drawais.ent@gmail.com" className="text-neutral-900 hover:text-amber-700 transition-colors">
                        drawais.ent@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-wider text-neutral-500 mb-1">Phone</p>
                      <p className="text-neutral-900">+92 346 6644 333</p>
                      <p className="text-neutral-900">+965-66287414</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-wider text-neutral-500 mb-1">Location</p>
                      <p className="text-neutral-900">Jaber Al-Ahmed Hospital</p>
                      <p className="text-neutral-600 text-sm">Kuwait</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Linkedin size={20} className="text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm uppercase tracking-wider text-neutral-500 mb-1">LinkedIn</p>
                      <a 
                        href="https://www.linkedin.com/in/drmawais" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-neutral-900 hover:text-amber-700 transition-colors flex items-center gap-1"
                      >
                        linkedin.com/in/drmawais
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-stone-200">
                <h4 className="text-lg font-light mb-4">Response Time</h4>
                <p className="text-neutral-600 text-sm">
                  I typically respond within 24-48 hours. For urgent clinical matters, please contact the hospital directly.
                </p>
              </div>

              <a
                href="mailto:drawais.ent@gmail.com"
                className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium group"
              >
                <Download size={18} />
                Download CV
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-light mb-2">Dr. Muhammad Awais Amin</h3>
              <p className="text-neutral-400 text-sm">ENT–HNS Surgeon | CEO & Founder | Healthtech & Clinical Workflow Optimization</p>
            </div>
            <div className="flex gap-4">
              <a 
                href="mailto:drawais.ent@gmail.com"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
              >
                <Mail size={18} />
              </a>
              <a 
                href="https://www.linkedin.com/in/drmawais"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-400">
            <p>© 2025 Dr. M. Awais Amin. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <span>CodifyCare</span>
              <span>|</span>
              <span>NursingHive</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App